import {CoinsSections_Levels_Slider} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wallet/v1/page/sections.types';
import {
  COLORS,
  HorizontalList,
  HorizontalListWrapper,
  Icon,
  Text,
  useSlider,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import coinsWalletBackground from '../../../assets/background-dark.png';
import {CoinsLevelsHeader} from './CoinsLevelsHeader';
import {normalizeCoinsLevel} from './utils/normalizeCoinsLevel';
import {useCoinsLevelSlider} from './utils/useCoinsLevelSlider';

export const CoinsLevelSlider: React.FC<
  CoinsSections_Levels_Slider & {title?: string}
> = ({levels, title}) => {
  const {scrollWrapperRef} = useCoinsLevelSlider(levels);
  const {next, prev, scrollToSlide, nextIsActive, prevIsActive} =
    useSlider(scrollWrapperRef);

  if (!levels.length) {
    return null;
  }

  return (
    <Wrapper data-testid="coinsWalletLevels">
      {levels.length > 3 && (
        <CoinsLevelsHeader
          title={title}
          next={next}
          prev={prev}
          scrollToSlide={scrollToSlide}
          nextIsActive={nextIsActive}
          prevIsActive={prevIsActive}
        />
      )}
      <StyledHorizontalList ref={scrollWrapperRef}>
        {levels.map((level, index) => {
          const {
            bonusValue,
            bonusDescription,
            bottomHint,
            color,
            leftIcon,
            rightIcon,
            rightIconColor,
          } = normalizeCoinsLevel(level);

          return (
            <CoinsLevel key={index} $hasOverflow={levels.length > 3}>
              <Header data-state={level.type?.$case}>
                <Row>
                  <BonusValue>
                    {leftIcon && (
                      <Icon asset={leftIcon} size="XS" fill={COLORS[color!]} />
                    )}
                    <Text size="px16" weight="heavy" color={color}>
                      {bonusValue}
                    </Text>
                  </BonusValue>
                  {rightIcon && (
                    <StatusIcon
                      asset={rightIcon}
                      size="XS"
                      fill={COLORS[rightIconColor!]}
                    />
                  )}
                </Row>
                <Description size="px10" weight="mediumHeavy" color={color}>
                  {bonusDescription}
                </Description>
              </Header>
              <Footer>
                <Text size="px10" color="Text.Grey">
                  {bottomHint}
                </Text>
              </Footer>
            </CoinsLevel>
          );
        })}
      </StyledHorizontalList>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledHorizontalList = styled(HorizontalList)`
  position: relative;
  display: flex;
  margin: 0 -16px;
  width: calc(100% + 32px);

  ${HorizontalListWrapper} {
    width: 100%;
    gap: 8px;
    overflow-y: hidden;
    padding: 0 16px;

    &::after {
      display: none;
    }
  }
`;

const CoinsLevel = styled.div<{$hasOverflow: boolean}>`
  display: flex;
  flex-direction: column;
  // if there's an overflow, slightly reduce the card size to make part of the next card visible
  flex: ${props =>
    `0 0 calc(100% / 3 - ${props.$hasOverflow ? '6px' : '8px'})`};
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 8px 12px 12px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border: 1px solid ${COLORS['Outline.Grey']};

  &[data-state='claimed'] {
    background-color: ${COLORS.White};
  }

  &[data-state='pending'] {
    background: url(${coinsWalletBackground});
    background-size: contain;
  }

  &[data-state='notReached'] {
    background: repeating-linear-gradient(
      135deg,
      ${COLORS.White},
      ${COLORS.White} 4px,
      ${COLORS['Fill.Grey.Primary']} 4px,
      ${COLORS['Fill.Grey.Primary']} 8px
    );
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;

const BonusValue = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;

  > span {
    letter-spacing: 0.04rem;
    line-height: 1.5;
  }
`;

const StatusIcon = styled(Icon)`
  margin-top: -6px;
`;

const Description = styled(Text)`
  white-space: nowrap;
`;

const Footer = styled.div`
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border: 1px solid ${COLORS['Outline.Grey']};
  border-top: none;
  background-color: ${COLORS.White};
  padding: 0 12px 3px 12px;
  flex: 1;
`;
